import React, { useState } from 'react';
import classNames from 'classnames';
import { Form as FinalForm } from 'react-final-form';
import { FiEdit2 } from 'react-icons/fi';

import css from './Sections.module.css';
import { Button, FieldLocationAutocompleteInput, Form, H4, Icons, Map } from '../../../components';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import {
  autocompletePlaceSelected,
  autocompleteSearchRequired,
  composeValidators,
} from '../../../util/validators';

const identity = v => v;
const HOME_PORT_ZOOM = 10;

const getNumber = value => {
  const number = typeof value === 'string' ? Number(value) : value;
  return typeof number === 'number' && Number.isFinite(number) ? number : null;
};

const getOriginCoordinates = value => {
  if (!value) return {};

  return {
    latitude: getNumber(value.latitude ?? value.lat),
    longitude: getNumber(value.longitude ?? value.lng),
  };
};

const parseAddressParts = address => {
  const rawParts = typeof address === 'string' ? address.split(',').map(part => part.trim()) : [];
  const parts = rawParts.filter(Boolean);
  const lastPart = parts[parts.length - 1] || '';
  const hasCountrySuffix = /^(us|usa|u\.s\.a\.|united states|canada)$/i.test(lastPart);
  const locationParts = hasCountrySuffix ? parts.slice(0, -1) : parts;
  const statePart = locationParts.length > 1 ? locationParts[locationParts.length - 1] : '';
  const state = statePart?.split(/\s+/)?.[0] || '';

  return {
    city:
      locationParts.length > 2
        ? locationParts[locationParts.length - 2] || ''
        : locationParts.length === 2
        ? locationParts[0] || ''
        : '',
    state,
  };
};

const getPlaceId = selectedPlace =>
  selectedPlace?.placeId ||
  selectedPlace?.place_id ||
  selectedPlace?.id ||
  selectedPlace?.predictionPlace?.placeId ||
  null;

const locationToHomePort = location => {
  if (!location) return null;

  const selectedPlace = location.selectedPlace || location;
  const origin = selectedPlace.origin || location.origin;
  const { latitude: locationLatitude, longitude: locationLongitude } = getOriginCoordinates(
    location
  );
  const { latitude: originLatitude, longitude: originLongitude } = getOriginCoordinates(origin);
  const latitude = locationLatitude ?? originLatitude;
  const longitude = locationLongitude ?? originLongitude;
  const address = location.address || selectedPlace.address || location.search || '';
  const addressParts = parseAddressParts(address);

  if (!address && latitude == null && longitude == null) {
    return null;
  }

  return {
    address,
    city: location.city || addressParts.city,
    state: location.state || addressParts.state,
    latitude,
    longitude,
    lat: latitude,
    lng: longitude,
    name: location.name || address?.split(',')?.[0]?.trim() || '',
    placeId: location.placeId || getPlaceId(selectedPlace),
  };
};

const homePortToFormValue = homePort => {
  if (!homePort) return null;

  return {
    search: homePort.address || '',
    predictions: [],
    selectedPlace: {
      address: homePort.address || '',
      origin: {
        lat: homePort.latitude ?? homePort.lat,
        lng: homePort.longitude ?? homePort.lng,
      },
    },
  };
};

const formValueToHomePort = value => {
  const selectedPlace = value?.selectedPlace;
  const origin = selectedPlace?.origin;
  const { latitude, longitude } = getOriginCoordinates(origin);
  const address = selectedPlace?.address || value?.search || '';
  const addressParts = parseAddressParts(address);

  return {
    address,
    city: addressParts.city,
    state: addressParts.state,
    latitude,
    longitude,
    lat: latitude,
    lng: longitude,
    name: address?.split(',')?.[0]?.trim() || '',
    placeId: getPlaceId(selectedPlace),
  };
};

const SectionHomePort = props => {
  const { publicData, editable = false, onUpdateProfile, updateInProgress = false } = props;
  const intl = useIntl();
  const [isEditing, setIsEditing] = useState(false);
  const [localError, setLocalError] = useState(null);
  const homePort =
    locationToHomePort(publicData?.homePort) || locationToHomePort(publicData?.location);
  const address = homePort?.address || '';
  const latitude = getNumber(homePort?.latitude);
  const longitude = getNumber(homePort?.longitude);
  const hasCoordinates = latitude != null && longitude != null;
  const center = hasCoordinates ? { lat: latitude, lng: longitude } : null;
  const placeName = homePort?.name || address?.split(',')?.[0]?.trim() || address;

  const saveHomePort = async values => {
    if (!editable || !onUpdateProfile) return;

    setLocalError(null);
    try {
      // publicData.homePort is public so visitors can see the user's home port map.
      await onUpdateProfile({ publicData: { homePort: formValueToHomePort(values.homePort) } });
      setIsEditing(false);
    } catch (e) {
      setLocalError(e);
    }
  };

  const removeHomePort = async () => {
    if (!editable || !onUpdateProfile) return;

    setLocalError(null);
    try {
      await onUpdateProfile({ publicData: { homePort: null } });
      setIsEditing(false);
    } catch (e) {
      setLocalError(e);
    }
  };

  if (!editable && !hasCoordinates && !address) {
    return null;
  }

  return (
    <div className={css.homePortSection}>
      <div className={css.sectionHeaderRow}>
        <H4 as="h3" className={css.captainsLogTitle}>
          <Icons name="location" />
          <FormattedMessage id="ProfilePage.SectionHomePort.homePortTitle" />
        </H4>
        {editable && !isEditing ? (
          <button
            type="button"
            className={classNames(css.iconActionButton, css.editIconActionButton)}
            onClick={() => setIsEditing(true)}
            disabled={updateInProgress}
            aria-label={
              homePort
                ? intl.formatMessage({ id: 'ProfilePage.SectionHomePort.editHomePort' })
                : intl.formatMessage({ id: 'ProfilePage.SectionHomePort.addHomePort' })
            }
            title={
              homePort
                ? intl.formatMessage({ id: 'ProfilePage.SectionHomePort.editHomePort' })
                : intl.formatMessage({ id: 'ProfilePage.SectionHomePort.addHomePort' })
            }
          >
            <FiEdit2 aria-hidden="true" focusable="false" />
          </button>
        ) : null}
      </div>

      {isEditing ? (
        <FinalForm
          onSubmit={saveHomePort}
          initialValues={{ homePort: homePortToFormValue(homePort) }}
          render={({ handleSubmit, invalid, values }) => {
            const addressRequiredMessage = intl.formatMessage({
              id: 'ProfilePage.SectionHomePort.addressRequired',
            });
            const addressNotRecognizedMessage = intl.formatMessage({
              id: 'ProfilePage.SectionHomePort.addressNotRecognized',
            });

            return (
              <Form className={css.homePortForm} onSubmit={handleSubmit}>
                <FieldLocationAutocompleteInput
                  rootClassName={css.homePortLocation}
                  className={css.homePortLocationField}
                  labelClassName={css.homePortLocationLabel}
                  iconClassName={css.homePortLocationIcon}
                  inputClassName={css.homePortLocationInput}
                  predictionsClassName={css.homePortPredictions}
                  name="homePort"
                  label={intl.formatMessage({ id: 'ProfilePage.SectionHomePort.homePortLabel' })}
                  placeholder={intl.formatMessage({
                    id: 'ProfilePage.SectionHomePort.homePortPlaceholder',
                  })}
                  useDefaultPredictions={false}
                  format={identity}
                  valueFromForm={values.homePort}
                  validate={composeValidators(
                    autocompleteSearchRequired(addressRequiredMessage),
                    autocompletePlaceSelected(addressNotRecognizedMessage)
                  )}
                />
                <div className={css.inlineEditActions}>
                  <Button
                    type="submit"
                    className={classNames(css.saveInlineButton, css.homePortActionButton)}
                    inProgress={updateInProgress}
                    disabled={invalid || updateInProgress}
                  >
                    <FormattedMessage id="ProfilePage.SectionHomePort.saveHomePort" />
                  </Button>
                  <Button
                    type="button"
                    className={classNames(css.inlineActionButton, css.homePortActionButton)}
                    onClick={() => setIsEditing(false)}
                    disabled={updateInProgress}
                  >
                    <FormattedMessage id="ProfilePage.SectionUserInfo.cancel" />
                  </Button>
                  {homePort ? (
                    <Button
                      type="button"
                      className={classNames(css.dangerInlineActionButton, css.homePortActionButton)}
                      onClick={removeHomePort}
                      disabled={updateInProgress}
                    >
                      <FormattedMessage id="ProfilePage.SectionHomePort.removeHomePort" />
                    </Button>
                  ) : null}
                </div>
              </Form>
            );
          }}
        />
      ) : hasCoordinates ? (
        <>
          <div className={css.mapPlaceholder}>
            <Map center={center} address={address} zoom={HOME_PORT_ZOOM} useStaticMap />
          </div>
          <div className={css.marinaInfo}>
            <div className={css.marinaName}>{placeName}</div>
            {address ? <div className={css.marinaLocation}>{address}</div> : null}
          </div>
        </>
      ) : (
        <p className={css.emptyState}>
          <FormattedMessage id="ProfilePage.SectionHomePort.noHomePort" />
        </p>
      )}
      {localError ? (
        <p className={css.inlineError}>
          <FormattedMessage id="ProfileSettingsForm.updateProfileFailed" />
        </p>
      ) : null}
    </div>
  );
};

export default SectionHomePort;
